/* eslint-disable */
import React from 'react'
import { Control, UseFormWatch, FieldValues } from 'react-hook-form'

import { Text } from '@/components/common/UI'
import { FileUploadControl } from '@/components/form-controls'
import { Icons } from '@/components/icons'
import {
    Chip,
    Button,
    Link,
    Tooltip,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    useDisclosure
} from '@/components/nextui'
import { useTranslation } from 'react-i18next'
import type { AspirantDocument } from '@/types/documents'
import { DocumentStatus } from '@/types/documents/enums'
import { useToast } from '@/context/ToastContext'
import { getFieldName } from '@/utils/documentUtils'

import { ICFES_RESULTS_SUBTEST_ID, IcfesDocumentInfo } from './IcfesDocumentInfo'

const SUBTESTS_WITHOUT_PENDING_BUTTON = new Set<number>([
    3, // Fotocopia del documento de identidad
    9, // Fotocopia documento de identidad
    13, // Fotocopia del Documento de Identidad
    19, // Fotocopia del documento de identidad
    22, // Copia pasaporte / Copia documento de identidad
    32, // Fotocopia del pasaporte
    33, // Fotocopia de visa de estudiante
    34, // Fotocopia cédula de extranjería
    37, // Fotocopia del pasaporte
    38, // Fotocopia de visa de estudiante
    39, // Fotocopia cédula de extranjería
    43, // Fotocopia del documento de identidad
    2, // Autorización informada datos menores de edad
    51 // Carta motivacional
])

// ID del documento de Carta motivacional para mostrar modal informativo
const CARTA_MOTIVACIONAL_ID = 51

interface DocumentCardProps<T extends FieldValues = any> {
    document: AspirantDocument
    control: Control<T>
    watch: UseFormWatch<T>
    uploadIndividually: boolean
    uploadInProgress: Record<string, boolean>
    onDeleteDocument: (idAspirantDocument?: number) => Promise<void>
    onUploadSingleDocument: (
        additionalDocumentId: number | null,
        sapDocumentSubtestId: number | null,
        file: File,
        markAsPending?: boolean,
        originIndex?: number,
        fieldKeyOverride?: string
    ) => Promise<boolean>
    onUploadWithHelp?: (
        additionalDocumentId: number | null,
        sapDocumentSubtestId: number | null,
        file: File
    ) => Promise<boolean>
    text: string
    fieldNameOverride?: string
    isPaymentCompleted?: boolean
}

export function DocumentCard<T extends FieldValues = any>({
    document,
    control,
    watch,
    uploadIndividually,
    uploadInProgress,
    onDeleteDocument,
    onUploadSingleDocument,
    onUploadWithHelp,
    text,
    fieldNameOverride,
    isPaymentCompleted = false
}: DocumentCardProps<T>): JSX.Element {
    const { t } = useTranslation()
    const { showToast } = useToast()
    const [isMarkingPending, setIsMarkingPending] = React.useState(false)
    const helpFileInputRef = React.useRef<HTMLInputElement>(null)
    const {
        isOpen: isCartaMotivacionalModalOpen,
        onOpen: onOpenCartaMotivacionalModal,
        onClose: onCloseCartaMotivacionalModal
    } = useDisclosure()

    // Verificar si es carta motivacional
    const isCartaMotivacional =
        document.idSapDocumentSubtest === CARTA_MOTIVACIONAL_ID

    // Estados del documento
    const isApproved =
        document.idDocumentStatus === DocumentStatus.Aprobado ||
        document.idDocumentStatus === DocumentStatus.VerificacionHumana
    const isPendingApprove =
        document.idDocumentStatus === DocumentStatus.AprobadoPendiente
    const isApprovedOrPending = isApproved || isPendingApprove
    const isReviewManual =
        document.idDocumentStatus === DocumentStatus.RevisionManual
    const isRejected = document.idDocumentStatus === DocumentStatus.Rechazado
    // Al volver a cargar, el documento queda en revisión con la observación
    // del rechazo anterior; no aplica al archivo nuevo
    const isAwaitingReview =
        document.idDocumentStatus === DocumentStatus.EnRevision ||
        document.idDocumentStatus === DocumentStatus.SubeTuDocumento
    const hasStatus = document.statusName !== null
    const hasExistingFile = Boolean(document.documentUrl)
    const baseFieldName = getFieldName(document)
    const fieldName = fieldNameOverride ? `${fieldNameOverride}` : baseFieldName
    const isUploading = uploadInProgress[fieldName] || false
    const shouldHidePendingButton =
        document.idSapDocumentSubtest !== null &&
        document.idSapDocumentSubtest !== undefined &&
        SUBTESTS_WITHOUT_PENDING_BUTTON.has(document.idSapDocumentSubtest)

    const isSolicitudDescuento =
        document.nameDocumentType === 'Solicitud de descuento'
    const normalizedDocumentName = (document.nameDocumentType || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
    const isContenidoProgramatico =
        normalizedDocumentName.includes('contenidos') &&
        normalizedDocumentName.includes('program')
    const isCertificadoNotas =
        normalizedDocumentName.includes('certificado') &&
        normalizedDocumentName.includes('notas')
    const isHomologationDocument = isContenidoProgramatico || isCertificadoNotas
    const maxUploadSizeMb = isContenidoProgramatico ? 30 : 5
    // Solo los documentos VERIFICADOS o con pago completado están completamente bloqueados
    const isFullyLocked =
        isApprovedOrPending || (isPaymentCompleted && isSolicitudDescuento)

    const documentTypesPlantilla = [
        'Autorización informada datos menores de edad',
        'Solicitud de descuento',
        'Plan Carta de plan E+'
    ]
    const needPlantilla = (): boolean => {
        return documentTypesPlantilla.includes(document.nameDocumentType)
    }
    const urlPlantilla = (): string => {
        if (
            document.nameDocumentType ===
            'Autorización informada datos menores de edad'
        ) {
            return 'https://universidadean.edu.co/sites/default/files/admisiones/autorizacion_informada_datos_menores_edad.pdf'
        }

        if (document.nameDocumentType === 'Plan Carta de plan E+') {
            // Documento almacenado en S3 (babylon-aspirants-commons-documents)
            return 'https://babylon-aspirants-commons-documents.s3.us-east-1.amazonaws.com/Carta_compromiso_Plan_E%2B_2025_2.cleaned.pdf'
        }

        // Solicitud de descuento
        return 'https://universidadean.edu.co/admisiones/convenios-corporativos'
    }

    const textPlantilla = (): string => {
        if (
            document.nameDocumentType ===
            'Autorización informada datos menores de edad'
        ) {
            return 'plantilla'
        }

        if (document.nameDocumentType === 'Plan Carta de plan E+') {
            return 'descargarCartaPlanEPlus'
        }

        return 'solicitudDescuento'
    }
    // Truncate observation if it's too long
    const truncateText = (text: string, maxLength: number = 400): string => {
        if (!text) {
            return ''
        }

        return text.length > maxLength
            ? text.substring(0, maxLength) + '...'
            : text
    }

    // Handle upload button click
    const handleUpload = async (e?: React.MouseEvent): Promise<void> => {
        // Prevenir comportamiento por defecto si viene de un evento
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        const file: any = watch(fieldName as any)

        if (
            file &&
            typeof file === 'object' &&
            'name' in file &&
            'size' in file &&
            'type' in file
        ) {
            console.log(`Attempting to upload file:`, file)
            const anyDoc: any = document as any
            const originIndex =
                anyDoc?.originIndex ??
                anyDoc?.origin_index ??
                anyDoc?.idHomologationProcessInstitution ??
                0

            await onUploadSingleDocument(
                document.idAdditionalDocument ?? null,
                document.idSapDocumentSubtest ?? null,
                file as File,
                false, // markAsPending
                Number(originIndex)
            )
        } else {
            console.log('No valid file selected')
        }
    }

    // Check if a file is selected
    const hasFileSelected = (): boolean => {
        const file: any = watch(fieldName as any)

        return !!(
            file &&
            typeof file === 'object' &&
            'name' in file &&
            'size' in file &&
            'type' in file
        )
    }

    // Manejar selección de archivo para documento pendiente
    const handlePendingFileSelect = async (
        event: React.ChangeEvent<HTMLInputElement>
    ): Promise<void> => {
        const file = event.target.files?.[0]
        if (!file || !onUploadWithHelp) return

        setIsMarkingPending(true)
        try {
            const success = await onUploadWithHelp(
                document.idAdditionalDocument ?? null,
                document.idSapDocumentSubtest ?? null,
                file
            )

            if (success) {
                showToast({
                    type: 'success',
                    message: t(`${text}.pendingReviewSuccess`)
                })
            }
        } catch (error) {
            console.error('Error uploading pending document:', error)
            showToast({
                type: 'error',
                message: t(`${text}.uploadError`)
            })
        } finally {
            setIsMarkingPending(false)
            event.target.value = ''
        }
    }

    // Render status chip
    const renderStatusChip = (): JSX.Element | null => {
        if (!hasStatus || !hasExistingFile) {
            return null
        }
        console.log('document.statusName', document.statusName)

        if (isApproved) {
            return (
                <Chip
                    color="success"
                    variant="flat"
                    startContent={<Icons.CheckCircle className="w-4 h-4" />}
                    className="max-w-full min-w-fit"
                >
                    <span
                        className="text-xs leading-tight break-words whitespace-normal text-left"
                        style={{
                            maxWidth: '120px',
                            wordBreak: 'break-word',
                            lineHeight: '1.2'
                        }}
                    >
                        {document.statusName || ''}
                    </span>
                </Chip>
            )
        } else if (isPendingApprove) {
            return (
                <Chip
                    color="warning"
                    variant="flat"
                    startContent={<Icons.Clock className="w-4 h-4" />}
                    className="max-w-full min-w-fit"
                >
                    <span
                        className="text-xs leading-tight break-words whitespace-normal text-left"
                        style={{
                            maxWidth: '120px',
                            wordBreak: 'break-word',
                            lineHeight: '1.2'
                        }}
                    >
                        {t(`${text}.approvedLabel`, {
                            defaultValue: 'Aprobado'
                        })}
                    </span>
                </Chip>
            )
        } else if (isRejected) {
            return (
                <Chip
                    color="danger"
                    variant="flat"
                    startContent={<Icons.Ban className="w-4 h-4" />}
                    className="max-w-full min-w-fit"
                >
                    <span
                        className="text-xs leading-tight break-words whitespace-normal text-left"
                        style={{
                            maxWidth: '120px',
                            wordBreak: 'break-word',
                            lineHeight: '1.2'
                        }}
                    >
                        {document.statusName || ''}
                    </span>
                </Chip>
            )
        } else {
            // Mostrar "Sube tus documentos" solo si es pending, sino mostrar el statusName del backend
            // Revisión manual: lo que la IA no aprueba queda para el equipo de Admisiones
            const displayText = isReviewManual
                ? t(`${text}.expertReviewLabel`, {
                      defaultValue: 'Revisión por experto'
                  })
                : document.statusName?.toLowerCase().includes('pending') ||
                    document.statusName?.toLowerCase().includes('pendiente')
                  ? 'Sube tu documento'
                  : document.statusName || ''

            return (
                <Chip
                    color="warning"
                    variant="flat"
                    startContent={<Icons.File className="w-4 h-4" />}
                    className="max-w-full min-w-fit"
                >
                    <span
                        className="text-xs leading-tight break-words whitespace-normal text-left"
                        style={{
                            maxWidth: '120px',
                            wordBreak: 'break-word',
                            lineHeight: '1.2'
                        }}
                    >
                        {displayText}
                    </span>
                </Chip>
            )
        }
    }

    // Render the upload button section
    const renderUploadButton = (): JSX.Element | null => {
        if (!uploadIndividually) {
            return null
        }

        return (
            <div className="mt-4 pt-3 border-t">
                {hasFileSelected() ? (
                    <Button
                        color="success"
                        size="md"
                        className="w-full"
                        isLoading={isUploading}
                        startContent={<Icons.Upload className="w-5 h-5" />}
                        onClick={(e) => handleUpload(e)}
                        disabled={isFullyLocked} // ← Solo deshabilitar si está verificado
                    >
                        {isUploading
                            ? t(`${text}.uploading`)
                            : document.documentUrl
                              ? `${t(`${text}.updateDocument`)} ${
                                    document.nameDocumentType
                                }`
                              : `${t(`${text}.uploadDocument`)} ${
                                    document.nameDocumentType
                                }`}
                    </Button>
                ) : (
                    <div className="flex flex-col gap-2">
                        <div className="p-2 border border-blue-200 rounded-md bg-blue-50">
                            <Text
                                className="text-sm text-blue-700 text-center"
                                text={t(`${text}.firstSelectFile`)}
                            />
                        </div>
                        <Button
                            color="primary"
                            size="md"
                            className="w-full opacity-50"
                            isDisabled={true}
                            startContent={<Icons.Upload className="w-5 h-5" />}
                        >
                            {document.documentUrl
                                ? t(`${text}.update`)
                                : t(`${text}.upload`)}
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString()
    }

    return (
        <article
            className={`p-3 border rounded-md transition-all duration-200 ${
                isApproved
                    ? 'bg-green-50 border-green-200'
                    : isPendingApprove
                      ? 'bg-yellow-50 border-yellow-200'
                      : isRejected
                        ? 'bg-red-50 border-red-200'
                        : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
            key={document.idDocumentType}
        >
            {/* Header compacto */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
                <div className="flex-1 min-w-0">
                    <p
                        className={`text-sm font-medium break-words hyphens-auto word-break ${
                            isApproved
                                ? 'text-green-800'
                                : isPendingApprove
                                  ? 'text-yellow-800'
                                  : isRejected
                                    ? 'text-red-800'
                                    : 'text-gray-800'
                        }`}
                        style={{
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            hyphens: 'auto'
                        }}
                    >
                        {document.nameDocumentType}
                        {document.isRequired && ' *'}
                    </p>
                </div>
                {hasStatus && (
                    <div className="flex-shrink-0 self-start">
                        {renderStatusChip()}
                    </div>
                )}
            </div>

            {/* Observación compacta - solo si existe, hay archivo y ya fue revisado */}
            {hasExistingFile &&
                !isAwaitingReview &&
                document.observation &&
                document.observation.length > 0 && (
                    <div className="mb-3">
                        <div
                            className={`p-2 rounded border-l-2 text-xs ${
                                isApproved
                                    ? 'bg-green-100 border-green-500 text-green-700'
                                    : isPendingApprove
                                      ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                                      : isRejected
                                        ? 'bg-red-100 border-red-500 text-red-700'
                                        : 'bg-gray-50 border-blue-500 text-gray-600'
                            }`}
                        >
                            {isRejected && document.observation.length > 100
                                ? 'El documento no cuenta con la estructura necesaria'
                                : truncateText(document.observation, 100)}
                        </div>
                    </div>
                )}

            {/* Certificado ICFES: fecha y registro declarados en Educación */}
            {document.idSapDocumentSubtest === ICFES_RESULTS_SUBTEST_ID && (
                <IcfesDocumentInfo />
            )}

            {/* File upload control */}
            <div>
                <FileUploadControl
                    name={fieldName}
                    control={control as any}
                    maxSize={maxUploadSizeMb}
                    allowedTypes={['.pdf', '.jpg', '.jpeg', '.png']}
                    existingFile={document.documentUrl || undefined}
                    displayName={document.nameDocumentType}
                    clearAfterSelect
                    onDeleteExisting={() => {
                        onDeleteDocument(document.idAspirantDocument)
                    }}
                    onFileSelect={(file) => {
                        if (file) {
                            onUploadSingleDocument(
                                document.idAdditionalDocument ?? null,
                                document.idSapDocumentSubtest ?? null,
                                file,
                                false, // markAsPending
                                undefined, // originIndex
                                fieldName // fieldKeyOverride
                            )
                        }
                    }}
                    onValidationError={(message) => {
                        showToast({ type: 'error', message })
                    }}
                    disabled={isFullyLocked || isReviewManual}
                />
            </div>

            {/* añadir url plantilla si existe para ese nombre de documento */}
            {needPlantilla() && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <Link
                        href={urlPlantilla()}
                        isExternal
                        showAnchorIcon
                    >
                        {t(`${text}.${textPlantilla()}`)}
                    </Link>
                </div>
            )}

            {/* Botón y modal informativo para Carta motivacional */}
            {isCartaMotivacional && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <Button
                        color="primary"
                        variant="light"
                        size="sm"
                        className="w-full justify-start text-left"
                        startContent={<Icons.IosAlert className="w-4 h-4" />}
                        onPress={onOpenCartaMotivacionalModal}
                    >
                        {t(`${text}.cartaMotivacionalTitle`)}
                    </Button>

                    <Modal
                        isOpen={isCartaMotivacionalModalOpen}
                        onClose={onCloseCartaMotivacionalModal}
                        placement="center"
                        size="lg"
                    >
                        <ModalContent>
                            {(onClose) => (
                                <>
                                    <ModalHeader className="flex flex-col gap-1">
                                        <span className="text-primary">
                                            {t(
                                                `${text}.cartaMotivacionalTitle`
                                            )}
                                        </span>
                                    </ModalHeader>
                                    <ModalBody>
                                        <p className="text-gray-700 text-sm leading-relaxed">
                                            {t(
                                                `${text}.cartaMotivacionalDescription`
                                            )}
                                        </p>
                                    </ModalBody>
                                    <ModalFooter className="flex justify-center">
                                        <Button
                                            color="primary"
                                            onPress={onClose}
                                        >
                                            Entendido
                                        </Button>
                                    </ModalFooter>
                                </>
                            )}
                        </ModalContent>
                    </Modal>
                </div>
            )}
            {/* Fechas compactas - solo si existen */}
            {document.createdAt && document.updatedAt && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="flex justify-between text-xs text-gray-500">
                        <span>Creado: {formatDate(document.createdAt)}</span>
                        <span>
                            Actualizado: {formatDate(document.updatedAt)}
                        </span>
                    </div>
                </div>
            )}

            {/* Botón para enviar documento pendiente */}
            {!isApprovedOrPending &&
                !isReviewManual &&
                onUploadWithHelp &&
                !document.idAdditionalDocument &&
                !shouldHidePendingButton && (
                    <div className="mt-3 pt-3 border-t border-green-100">
                        <input
                            type="file"
                            ref={helpFileInputRef}
                            accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            onChange={handlePendingFileSelect}
                        />

                        <Tooltip
                            content={t(`${text}.markPendingReviewTooltip`)}
                            className="max-w-xs"
                        >
                            <Button
                                size="sm"
                                color={isRejected ? 'danger' : 'success'}
                                variant="flat"
                                className="w-full"
                                isLoading={isMarkingPending}
                                startContent={
                                    !isMarkingPending && (
                                        <Icons.FileCheck className="w-4 h-4" />
                                    )
                                }
                                onClick={() =>
                                    helpFileInputRef.current?.click()
                                }
                            >
                                {isMarkingPending
                                    ? t(`${text}.markingPendingReview`)
                                    : t(`${text}.markPendingReview`)}
                            </Button>
                        </Tooltip>
                    </div>
                )}

            {/* Mensaje informativo si ya está en revisión manual */}
            {isReviewManual && (
                <div className="mt-3 pt-3 border-t border-green-100">
                    <div className="flex items-start gap-2 p-2 bg-orange-50 rounded-md border border-orange-200">
                        <Icons.IosAlert className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                        <Text
                            className="text-xs text-orange-700"
                            text={
                                isSolicitudDescuento
                                    ? t(`${text}.needsManualReviewDiscount`)
                                    : isHomologationDocument
                                      ? t(
                                            `${text}.needsManualReviewHomologation`
                                        )
                                      : t(`${text}.needsManualReview`)
                            }
                        />
                    </div>
                </div>
            )}
        </article>
    )
}
